// Progress ribbon: every step of the current path, its status, and a jump button once unlocked
// (stories 7, 9-11). The status circle alone carries progress: empty (not started or on defaults),
// half filled (some edits), green check (every input answered). A pure view of `buildSteps` output; no store access, so it stays easy to test.

import { useCallback, useEffect, useRef, useState } from "react";
import { Ph } from "../Icon";
import type { Step, StepKind, StepStatus } from "../../flow/step-model";

const STATUS_LABEL: Record<StepStatus, string> = {
  "not-started": "Not started",
  "in-progress": "In progress",
  "using-defaults": "Using Clay defaults",
  complete: "Complete",
};

/** The ribbon's three sections, each under a small eyebrow, separated by chevrons. */
type Section = "Setup" | "Use cases" | "Summary";
const SECTION_OF: Record<StepKind, Section> = {
  title: "Setup",
  account: "Setup",
  selection: "Setup",
  deal: "Setup",
  "use-case": "Use cases",
  review: "Summary",
  roi: "Summary",
};

/** Consecutive steps grouped by section, in ribbon order. */
export function ribbonSections(steps: Step[]): { section: Section; steps: Step[] }[] {
  const out: { section: Section; steps: Step[] }[] = [];
  for (const step of steps) {
    const section = SECTION_OF[step.kind];
    const last = out[out.length - 1];
    if (last?.section === section) last.steps.push(step);
    else out.push({ section, steps: [step] });
  }
  return out;
}

interface ProgressRibbonProps {
  steps: Step[];
  currentStepId: string;
  onJump: (stepId: string) => void;
}

export function ProgressRibbon({
  steps,
  currentStepId,
  onJump,
}: ProgressRibbonProps) {
  const listRef = useRef<HTMLOListElement>(null);
  // Which edges have more steps scrolled out of view: drives the edge fades.
  const [more, setMore] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setMore((m) =>
      m.left === left && m.right === right ? m : { left, right },
    );
  }, []);

  // With many use cases the ribbon overflows: keep the current step visible when it changes.
  // Scroll only the ribbon itself — `scrollIntoView` also scrolls every scrollable ancestor,
  // which jumped the page to the top whenever a use case was (de)selected.
  useEffect(() => {
    const el = listRef.current;
    const cur = el?.querySelector<HTMLElement>('[aria-current="step"]');
    if (el && cur) {
      const list = el.getBoundingClientRect();
      const step = cur.getBoundingClientRect();
      if (step.left < list.left) el.scrollLeft -= list.left - step.left + 24;
      else if (step.right > list.right) el.scrollLeft += step.right - list.right + 24;
    }
    measure();
  }, [currentStepId, steps.length, measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <div
      className="vc3-ribbon-wrap"
      data-more-left={more.left}
      data-more-right={more.right}
    >
      <ol
        ref={listRef}
        className="vc3-ribbon"
        aria-label="Progress"
        onScroll={measure}
      >
        {ribbonSections(steps).map((group, gi) => (
          <li key={group.section} className="vc3-ribbon-section">
            {gi > 0 && (
              <span className="vc3-ribbon-chev" aria-hidden>
                <Ph name="caret-right" size={16} />
              </span>
            )}
            <div className="vc3-ribbon-group">
              <span className="vc3-ribbon-eyebrow">{group.section}</span>
              <ol className="vc3-ribbon-steps" aria-label={group.section}>
                {group.steps.map((step) => {
                  const current = step.id === currentStepId;
                  return (
                    <li key={step.id}>
                      <button
                        type="button"
                        className="vc3-step"
                        data-status={step.status}
                        disabled={step.locked}
                        aria-current={current ? "step" : undefined}
                        aria-label={`${step.label}: ${step.locked ? "locked until an account or demo mode is chosen" : STATUS_LABEL[step.status]}`}
                        title={
                          step.locked
                            ? "Choose an account or demo mode first"
                            : STATUS_LABEL[step.status]
                        }
                        onClick={() => onJump(step.id)}
                      >
                        <span className="vc3-step-dot" aria-hidden>
                          {step.status === "complete" && (
                            <Ph name="check" size={11} bold />
                          )}
                        </span>
                        <span>{step.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
